/**
 * Advanced API client with interceptors, caching, retry logic, and more
 * Now powered by Axios for robust HTTP handling
 */

import { HttpClient } from './http-client'
import { CacheManager } from '../cache/cache-manager'
import { RetryManager } from '../retry/retry-manager'
import { RateLimiter } from '../utils/rate-limiter'
import type { ApiClientConfig, RequestConfig, ApiResponse, ApiError } from '../types'
import type { AxiosResponse } from 'axios'

/**
 * Advanced API client with comprehensive features using Axios
 */
export class ApiClient {
  private httpClient: HttpClient
  private cacheManager?: CacheManager
  private retryManager?: RetryManager
  private rateLimiter?: RateLimiter
  private config: ApiClientConfig
  private interceptorIds: number[] = []

  constructor(config: ApiClientConfig = {}) {
    this.config = config
    this.httpClient = new HttpClient(config as Partial<RequestConfig>)

    // Initialize optional features
    if (config.cache?.enabled) {
      this.cacheManager = new CacheManager(config.cache)
    }

    if (config.retry) {
      this.retryManager = new RetryManager(config.retry)
    }

    if (config.rateLimit) {
      this.rateLimiter = new RateLimiter(config.rateLimit)
    }

    // Set up Axios interceptors
    this.setupAxiosInterceptors()
  }

  /**
   * Set up Axios interceptors for caching, rate limiting, and event handling
   */
  private setupAxiosInterceptors(): void {
    // Request interceptor for rate limiting and caching
    const requestInterceptorId = this.httpClient.addRequestInterceptor(
      async (config: any) => {
        // Apply rate limiting
        if (this.rateLimiter) {
          await this.rateLimiter.acquire()
        }

        // Call event handler
        if (this.config.onRequest) {
          this.config.onRequest(config as RequestConfig)
        }

        return config
      },
      (error: any) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor for caching and event handling
    const responseInterceptorId = this.httpClient.addResponseInterceptor(
      async (response: AxiosResponse) => {
        const apiResponse = response as unknown as ApiResponse<any>

        // Call event handler
        if (this.config.onResponse) {
          this.config.onResponse(apiResponse)
        }

        // Cache the response if applicable
        if (
          this.cacheManager &&
          this.shouldCacheResponse(response.config as unknown as RequestConfig, apiResponse)
        ) {
          await this.cacheManager.set(response.config as unknown as RequestConfig, apiResponse)
        }

        return response
      },
      (error: any) => {
        // Call error event handler
        if (this.config.onError) {
          this.config.onError(error as ApiError)
        }

        return Promise.reject(error)
      }
    )

    this.interceptorIds.push(requestInterceptorId, responseInterceptorId)
  }

  /**
   * Makes an HTTP request with all features applied
   */
  async request<T = any>(config: RequestConfig): Promise<ApiResponse<T>> {
    // Check cache first
    if (this.cacheManager && this.shouldUseCache(config)) {
      const cached = await this.cacheManager.get<T>(config)
      if (cached) {
        return cached
      }
    }

    // Execute request with retry logic if configured
    const executeRequest = async (): Promise<ApiResponse<T>> => {
      return await this.httpClient.request<T>(config)
    }

    if (this.retryManager) {
      return await this.retryManager.execute(executeRequest)
    } else {
      return await executeRequest()
    }
  }

  /**
   * GET request
   */
  async get<T = any>(url: string, config?: Partial<RequestConfig>): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, url, method: 'GET' })
  }

  /**
   * POST request
   */
  async post<T = any>(
    url: string,
    data?: any,
    config?: Partial<RequestConfig>
  ): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, url, method: 'POST', data })
  }

  /**
   * PUT request
   */
  async put<T = any>(
    url: string,
    data?: any,
    config?: Partial<RequestConfig>
  ): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, url, method: 'PUT', data })
  }

  /**
   * PATCH request
   */
  async patch<T = any>(
    url: string,
    data?: any,
    config?: Partial<RequestConfig>
  ): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, url, method: 'PATCH', data })
  }

  /**
   * DELETE request
   */
  async delete<T = any>(url: string, config?: Partial<RequestConfig>): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, url, method: 'DELETE' })
  }

  /**
   * HEAD request
   */
  async head(url: string, config?: Partial<RequestConfig>): Promise<ApiResponse<void>> {
    return this.request<void>({ ...config, url, method: 'HEAD' })
  }

  /**
   * OPTIONS request
   */
  async options<T = any>(url: string, config?: Partial<RequestConfig>): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, url, method: 'OPTIONS' })
  }

  /**
   * Adds a request interceptor using Axios
   */
  addRequestInterceptor(
    onFulfilled?: (config: any) => any | Promise<any>,
    onRejected?: (error: any) => any
  ): number {
    return this.httpClient.addRequestInterceptor(onFulfilled, onRejected)
  }

  /**
   * Adds a response interceptor using Axios
   */
  addResponseInterceptor(
    onFulfilled?: (response: any) => any | Promise<any>,
    onRejected?: (error: any) => any
  ): number {
    return this.httpClient.addResponseInterceptor(onFulfilled, onRejected)
  }

  /**
   * Removes a request interceptor
   */
  removeRequestInterceptor(id: number): void {
    this.httpClient.removeRequestInterceptor(id)
  }

  /**
   * Removes a response interceptor
   */
  removeResponseInterceptor(id: number): void {
    this.httpClient.removeResponseInterceptor(id)
  }

  /**
   * Clears all custom interceptors (keeps built-in ones)
   */
  clearInterceptors(): void {
    // Note: This only clears custom interceptors, not the built-in ones for caching/rate limiting
    // The built-in interceptors are managed internally
  }

  /**
   * Clears cache
   */
  clearCache(): void {
    if (this.cacheManager) {
      this.cacheManager.clear()
    }
  }

  /**
   * Gets cache statistics
   */
  getCacheStats(): any {
    return this.cacheManager?.getStats() || null
  }

  /**
   * Updates client configuration
   */
  updateConfig(config: Partial<ApiClientConfig>): void {
    this.config = { ...this.config, ...config }

    // Clear existing interceptors
    this.interceptorIds.forEach(id => {
      this.httpClient.removeRequestInterceptor(id)
      this.httpClient.removeResponseInterceptor(id)
    })
    this.interceptorIds = []

    // Update HTTP client
    this.httpClient = new HttpClient(this.config as Partial<RequestConfig>)

    // Re-setup interceptors with new config
    this.setupAxiosInterceptors()

    // Update cache manager
    if (config.cache && this.cacheManager) {
      this.cacheManager.updateConfig(config.cache)
    }

    // Update retry manager
    if (config.retry && this.retryManager) {
      this.retryManager.updateConfig(config.retry)
    }

    // Update rate limiter
    if (config.rateLimit && this.rateLimiter) {
      this.rateLimiter.updateConfig(config.rateLimit)
    }
  }

  /**
   * Gets current configuration
   */
  getConfig(): ApiClientConfig {
    return { ...this.config }
  }

  /**
   * Gets the underlying Axios instance for advanced usage
   */
  getAxiosInstance() {
    return this.httpClient.getAxiosInstance()
  }

  /**
   * Gets the underlying HTTP client
   */
  getHttpClient(): HttpClient {
    return this.httpClient
  }

  /**
   * Determines if cache should be used for request
   */
  private shouldUseCache(config: RequestConfig): boolean {
    if (!this.cacheManager) return false

    // Only cache GET requests by default
    if (config.method && config.method !== 'GET') return false

    // Check custom cache condition
    if (this.config.cache?.shouldCache) {
      return this.config.cache.shouldCache(config)
    }

    return true
  }

  /**
   * Determines if response should be cached
   */
  private shouldCacheResponse(config: RequestConfig, response: ApiResponse): boolean {
    if (!this.cacheManager) return false

    // Don't cache error responses
    if (response.status >= 400) return false

    return this.shouldUseCache(config)
  }
}
